import { DebugLogger } from '@affine/debug';
import { LiveData, Store } from '@toeverything/infra';
import { Doc as YDoc } from 'yjs';

import type { DefaultServerService } from '../../cloud';
import { UserDBService } from '../../userspace';
import type { UserDBWithTables } from '../../userspace/entities/user-db';
import {
  ACCOUNT_TEMPLATE_CONTENT_VERSION,
  accountTemplateDocId,
} from '../constants';
import type {
  AccountTemplateBlob,
  AccountTemplateContent,
  AccountTemplatePayload,
} from '../type';

const logger = new DebugLogger('AccountTemplateContentStore');

/**
 * Content of account-level templates. Each template owns one userspace doc
 * (guid `account-template:<id>`), connected on demand and released right after
 * — the metadata table is what stays loaded.
 */
export class AccountTemplateContentStore extends Store {
  constructor(private readonly defaultServerService: DefaultServerService) {
    super();
  }

  private _db$: LiveData<UserDBWithTables | null> | null = null;

  private get db$(): LiveData<UserDBWithTables | null> {
    if (!this._db$) {
      try {
        const userDBService =
          this.defaultServerService.server.scope.get(UserDBService);
        this._db$ = userDBService.currentUserDB.db$;
      } catch {
        this._db$ = new LiveData<UserDBWithTables | null>(null);
      }
    }
    return this._db$;
  }

  /** serializes concurrent access to the same template doc */
  private readonly queues = new Map<string, Promise<unknown>>();

  private enqueue<T>(templateId: string, task: () => Promise<T>): Promise<T> {
    const prev = this.queues.get(templateId) ?? Promise.resolve();
    const next = prev.then(task, task);
    this.queues.set(
      templateId,
      next.catch(() => undefined)
    );
    return next;
  }

  /**
   * Connect the template's doc, run `fn`, then disconnect. The doc is only
   * materialized in memory for the duration of the call.
   */
  private async withDoc<T>(
    templateId: string,
    fn: (ydoc: YDoc) => Promise<T> | T
  ): Promise<T> {
    const db = this.db$.value;
    if (!db) throw new Error('Not signed in');

    const frontend = db.engine.client.docFrontend;
    const guid = accountTemplateDocId(templateId);
    const ydoc = new YDoc({ guid });
    frontend.connectDoc(ydoc);
    const removePriority = frontend.addPriority(guid, 100);
    try {
      await frontend.waitForDocLoaded(guid);
      return await fn(ydoc);
    } finally {
      removePriority();
      // destroying the doc disconnects it from the frontend
      ydoc.destroy();
    }
  }

  async load(templateId: string): Promise<AccountTemplateContent | null> {
    return this.enqueue(templateId, () =>
      this.withDoc(templateId, ydoc => {
        const templateMap = ydoc.getMap('template');
        const raw = templateMap.get('payload');
        if (typeof raw !== 'string') return null;

        let payload: AccountTemplatePayload;
        try {
          // always a fresh parse: `replaceIdMiddleware` mutates the snapshot
          // in place, so a shared object could not be applied twice
          payload = JSON.parse(raw) as AccountTemplatePayload;
        } catch (e) {
          logger.error('Failed to parse account template payload', {
            templateId,
            error: e,
          });
          return null;
        }

        const blobsMap = ydoc.getMap('blobs');
        const metaMap = ydoc.getMap('blobsMeta');
        const blobs = new Map<string, AccountTemplateBlob>();
        for (const blobId of payload.blobs ?? []) {
          const bin = blobsMap.get(blobId);
          if (!(bin instanceof Uint8Array)) continue;
          const meta = metaMap.get(blobId) as
            | { mime?: string; name?: string }
            | undefined;
          blobs.set(blobId, {
            // copy out of the yjs doc before it is destroyed
            bin: new Uint8Array(bin),
            mime: meta?.mime ?? 'application/octet-stream',
            name: meta?.name,
          });
        }

        return { payload, blobs };
      })
    );
  }

  async save(
    templateId: string,
    payload: AccountTemplatePayload,
    blobs: Map<string, AccountTemplateBlob>
  ): Promise<void> {
    await this.enqueue(templateId, () =>
      this.withDoc(templateId, async ydoc => {
        ydoc.transact(() => {
          const templateMap = ydoc.getMap('template');
          const blobsMap = ydoc.getMap('blobs');
          const metaMap = ydoc.getMap('blobsMeta');

          blobsMap.clear();
          metaMap.clear();
          for (const [blobId, blob] of blobs) {
            blobsMap.set(blobId, blob.bin);
            metaMap.set(blobId, {
              mime: blob.mime,
              name: blob.name,
              size: blob.bin.byteLength,
            });
          }

          templateMap.set('version', ACCOUNT_TEMPLATE_CONTENT_VERSION);
          templateMap.set('payload', JSON.stringify(payload));
        });

        // report success only once the write left the client; still inside
        // `withDoc`, so the doc is connected while syncing
        const db = this.db$.value;
        await db?.engine.client.docFrontend.waitForSynced(
          accountTemplateDocId(templateId)
        );
      })
    );
  }

  /**
   * There is no client-side API to drop a userspace doc, so emptying it is the
   * closest we get. The metadata row is what makes a template visible.
   */
  async clear(templateId: string): Promise<void> {
    await this.enqueue(templateId, () =>
      this.withDoc(templateId, ydoc => {
        ydoc.transact(() => {
          ydoc.getMap('template').clear();
          ydoc.getMap('blobs').clear();
          ydoc.getMap('blobsMeta').clear();
        });
      })
    );
  }
}
